// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import { Link, useHistory } from "react-router-dom";
import { ROUTE_DEFINITIONS, getPath } from "../../routes/routes";
import React, { memo, useContext } from "react";
import {
  validateBoxChecked,
  validateName,
  validateZipCode,
} from "../ui/Questionnaire/validators/QuestionnaireValidators";

import { CustomerContext } from "components/App/DashboardApp";
import Grid from "@material-ui/core/Grid";
import QuestionnaireForm from "../ui/Questionnaire/Questionnaire";
import QuestionnaireInput from "components/ui/Questionnaire/QuestionnaireInput";
import addressStreetTypeAheadFomatter from "./addressStreetTypeAheadFormatter";
import addressTypeAhead from "./addressTypeahead";
import { confirmAlert } from "../../utils/confirmAlert";
import { createNewTestRun } from "store/testrun";
import { getFirebaseApp } from "../Firebase/Firebase";
import getGeoLocationForInput from "components/PlacesAPI/getGeoLocationForInput";
import { getTermsAndConditions } from "../TermsAndConditions/TermsAndConditions";
import { useAppDispatch } from "store";
import { useAuthState } from "../../store/auth";
import { useTestRunConfig } from "components/TestRun/useTestRunConfig";
import { useTheme } from "@material-ui/core";
import { zipCodeFormatter } from "../ui/Questionnaire/QuestionnaireFormatters";

export const PROFILE_CREATION_FORM = "profileCreationForm";

export interface ProfileCreationPanelProps {
  onFormReady: (formReady: boolean) => void;
}

export default memo((props: ProfileCreationPanelProps) => {
  const { auth } = useAuthState();
  const { getStartStep } = useTestRunConfig();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const orgId = useContext(CustomerContext).id;

  const QuestionnaireFormMemoized = memo(QuestionnaireForm);
  const history = useHistory();
  const { onFormReady } = props;

  return (
    <QuestionnaireFormMemoized
      formId={PROFILE_CREATION_FORM}
      onFormReady={onFormReady}
      onSubmit={async (event, questions) => {
        try {
          event.preventDefault();
          const firebaseApp = await getFirebaseApp();
          const profile: any = {};
          Object.getOwnPropertyNames(questions).forEach(fieldname => {
            profile[fieldname] = questions[fieldname].value || null;
          });

          await getGeoLocationForInput(
            [
              questions.address1.value,
              questions.city.value,
              questions.state.value,
              "USA",
            ].join(",")
          )
            .then(results => {
              const result = results[0];

              if (result === undefined) {
                throw Error("No corresponding geolocation found");
              }

              profile.lattitude = result.geometry.location.lat();
              profile.longitude = result.geometry.location.lng();
            })
            .catch(err => {
              throw err;
            });

          const profileDocument = await firebaseApp.addProfileToUser({
            orgId,
            profile: profile,
            userUID: auth!.uid,
          });

          const testRunID = await dispatch(
            createNewTestRun({
              profileUID: profileDocument.id,
              orgId,
              userUID: auth!.uid,
              startStep: getStartStep(),
            })
          );

          const route = { ...ROUTE_DEFINITIONS.PERFORMTEST };
          route.path = route.path
            .replace(":testRunUID", testRunID)
            .replace(":step", getStartStep());
          history.push(getPath(route));
        } catch (e) {
          confirmAlert({ title: "oops", message: e.message, theme });
        }
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <QuestionnaireInput
            icon="user"
            label="First Name"
            mandatory={true}
            name="firstname"
            type="text"
            validators={[validateName]}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <QuestionnaireInput
            icon="user"
            label="Last Name"
            mandatory={true}
            name="lastname"
            type="text"
            validators={[validateName]}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <QuestionnaireInput
            formatter={addressStreetTypeAheadFomatter}
            icon="home"
            label="Street Address"
            mandatory={true}
            name="address1"
            type="text"
            validators={[validateName]}
            typeahead={{
              callFunction: addressTypeAhead,
              values: [],
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <QuestionnaireInput
            formatter={zipCodeFormatter}
            label="ZIP Code"
            mandatory={true}
            name="zipcode"
            type="text"
            validators={[validateZipCode]}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <QuestionnaireInput
            label="State"
            mandatory={true}
            name="state"
            placeholder="Select a State"
            selectOptions={[
              { value: "AL", name: "AL" },
              { value: "AK", name: "AK" },
              { value: "AS", name: "AS" },
              { value: "AZ", name: "AZ" },
              { value: "AR", name: "AR" },
              { value: "CA", name: "CA" },
              { value: "CO", name: "CO" },
              { value: "CT", name: "CT" },
              { value: "DE", name: "DE" },
              { value: "DC", name: "DC" },
              { value: "FM", name: "FM" },
              { value: "FL", name: "FL" },
              { value: "GA", name: "GA" },
              { value: "GU", name: "GU" },
              { value: "HI", name: "HI" },
              { value: "ID", name: "ID" },
              { value: "IL", name: "IL" },
              { value: "IN", name: "IN" },
              { value: "IA", name: "IA" },
              { value: "KS", name: "KS" },
              { value: "KY", name: "KY" },
              { value: "LA", name: "LA" },
              { value: "ME", name: "ME" },
              { value: "MH", name: "MH" },
              { value: "MD", name: "MD" },
              { value: "MA", name: "MA" },
              { value: "MI", name: "MI" },
              { value: "MN", name: "MN" },
              { value: "MS", name: "MS" },
              { value: "MO", name: "MO" },
              { value: "MT", name: "MT" },
              { value: "NE", name: "NE" },
              { value: "NV", name: "NV" },
              { value: "NH", name: "NH" },
              { value: "NJ", name: "NJ" },
              { value: "NM", name: "NM" },
              { value: "NY", name: "NY" },
              { value: "NC", name: "NC" },
              { value: "ND", name: "ND" },
              { value: "MP", name: "MP" },
              { value: "OH", name: "OH" },
              { value: "OK", name: "OK" },
              { value: "OR", name: "OR" },
              { value: "PW", name: "PW" },
              { value: "PA", name: "PA" },
              { value: "PR", name: "PR" },
              { value: "RI", name: "RI" },
              { value: "SC", name: "SC" },
              { value: "SD", name: "SD" },
              { value: "TN", name: "TN" },
              { value: "TX", name: "TX" },
              { value: "UT", name: "UT" },
              { value: "VT", name: "VT" },
              { value: "VI", name: "VI" },
              { value: "VA", name: "VA" },
              { value: "WA", name: "WA" },
              { value: "WV", name: "WV" },
              { value: "WI", name: "WI" },
              { value: "WY", name: "WY" },
            ]}
            type="select"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <QuestionnaireInput
            icon="city"
            label="City"
            mandatory={true}
            name="city"
            type="text"
          />
        </Grid>
      </Grid>
      <QuestionnaireInput
        label={
          <>
            I have read and understand the{" "}
            <Link
              to={getPath(ROUTE_DEFINITIONS.TERMSANDCONDITIONS)}
              onClick={event => {
                event.preventDefault();
                confirmAlert({
                  title: "OpenRDT Terms and Conditions",
                  message: getTermsAndConditions(),
                  theme,
                });
              }}
            >
              terms and conditions
            </Link>
            .
          </>
        }
        mandatory={true}
        name="eula"
        type="checkbox"
        validators={[validateBoxChecked]}
      />
    </QuestionnaireFormMemoized>
  );
});
